/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Dropdown, FormControl, Spinner} from 'react-bootstrap'
import lodash from 'lodash'
import {OecdDescription} from '../../oecd-descriptions/core/_models'

type OecdDropdownProps = {
  onSelect: (code: string | null) => void
  disabled?: boolean
  value?: string | null
  oecdList: OecdDescription[]
  placeholder?: string
}

const LIMIT = 50

const buildLabel = (o: OecdDescription): string => {
  const truncDesc = o.desc ? o.desc.substring(0, 80) : ''
  return truncDesc ? `${o.code} — ${truncDesc}` : o.code
}

const OecdDropdown: FC<OecdDropdownProps> = ({
  onSelect,
  disabled = false,
  value,
  oecdList,
  placeholder = 'Search by code or description...',
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [displayedCount, setDisplayedCount] = useState<number>(LIMIT)
  const [filteredList, setFilteredList] = useState<OecdDescription[]>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  // Filter the full list client-side
  const applyFilter = useCallback(
    (search: string) => {
      const term = search.toLowerCase()
      const filtered = term
        ? oecdList.filter(
            (o) =>
              o.code.toLowerCase().includes(term) ||
              (o.desc && o.desc.toLowerCase().includes(term))
          )
        : oecdList
      setFilteredList(filtered)
    },
    [oecdList]
  )

  // Debounced search
  const debouncedSearch = useCallback(
    lodash.debounce((search: string) => {
      setDisplayedCount(LIMIT)
      applyFilter(search)
    }, 300),
    [applyFilter]
  )

  // Init / reload when oecdList changes
  useEffect(() => {
    applyFilter(searchTerm)
  }, [oecdList])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value
    setSearchTerm(val)
    debouncedSearch(val)
  }

  const handleLoadMore = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDisplayedCount((prev) => prev + LIMIT)
  }

  const handleSelect = (code: string) => {
    onSelect(code)
    setIsDropdownOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect(null)
    setIsDropdownOpen(false)
  }

  const handleToggle = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
    if (!isOpen) {
      setSearchTerm('')
      setDisplayedCount(LIMIT)
      applyFilter('')
    }
  }

  // Resolve selected label
  const selectedItem = value ? oecdList.find((o) => o.code === value) : null
  const selectedLabel = selectedItem ? buildLabel(selectedItem) : null

  const visibleItems = filteredList.slice(0, displayedCount)
  const hasMore = filteredList.length > displayedCount

  return (
    <Dropdown onToggle={handleToggle} show={isDropdownOpen}>
      <Dropdown.Toggle
        variant='label-dropdown'
        id=''
        className='btn btn-sm btn-bold dropdown-toggle fw-normal text-start w-100'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '44px',
          height: 'auto',
          whiteSpace: 'normal',
        }}
        disabled={disabled}
      >
        <div
          style={{
            flex: 1,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            textAlign: 'left',
          }}
        >
          {selectedLabel ?? <span className='text-muted'>{placeholder}</span>}
        </div>
        {value && !disabled && (
          <span
            className='ms-2 text-muted'
            style={{cursor: 'pointer', flexShrink: 0}}
            onClick={handleClear}
            title='Clear'
          >
            ×
          </span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className='dropdown-econ-menu w-100'
        style={{maxHeight: '300px', overflowY: 'auto'}}
      >
        {/* Sticky search bar */}
        <div
          style={{
            padding: '4px 8px',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
          }}
        >
          <FormControl
            type='text'
            size='sm'
            placeholder='Search by code or description...'
            className='dropdown-econ-search-bar'
            value={searchTerm}
            onChange={handleSearch}
            autoFocus
          />
        </div>

        {/* Items */}
        {visibleItems.length === 0 ? (
          <div className='text-muted text-center p-2' style={{fontSize: '0.85rem'}}>
            No results found.
          </div>
        ) : (
          <>
            {visibleItems.map((o) => (
              <Dropdown.Item
                key={o.code}
                role='menuitem'
                size='sm'
                onClick={() => handleSelect(o.code)}
                title={buildLabel(o)}
                style={{whiteSpace: 'normal'}}
                active={o.code === value}
              >
                {buildLabel(o)}
              </Dropdown.Item>
            ))}

            {/* Load More button */}
            {hasMore && (
              <div className='text-center p-2'>
                <button
                  className='btn btn-sm btn-primary w-100'
                  onClick={handleLoadMore}
                >
                  Load More ({filteredList.length - displayedCount} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default OecdDropdown
